import { useState } from "react";
import { createUseStyles } from "react-jss";
import type { Reservation } from "../../../../common/src";
import type { VetBoatsTheme } from "../../theme";
import { formatDays, formatRange, formatTimestamp } from "../../utils/format";
import { Badge } from "../shared/Badge";
import { Button } from "../shared/Button";
import { ConfirmButton } from "../shared/ConfirmButton";
import { ChecklistPanel } from "./ChecklistPanel";
import { GuestWaiverPanel } from "./GuestWaiverPanel";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  actions: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    alignItems: "flex-start",
  },
  fullWidth: { flexBasis: "100%" },
  list: { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 10 },
  item: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    padding: 14,
    border: `1px solid ${theme.color.border}`,
    borderRadius: theme.radius,
    flexWrap: "wrap",
  },
  cancelled: { opacity: 0.65 },
  dates: { fontWeight: 700, marginBottom: 4 },
  meta: {
    color: theme.color.muted,
    fontSize: 14,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  notes: { margin: "6px 0 0", fontSize: 14, whiteSpace: "pre-line" },
}));

interface ReservationListProps {
  reservations: Reservation[];
  today: string;
  showPerson: boolean;
  cancellingId: number | undefined;
  onCancel: (id: number) => void;
}

type PanelKind = "guests" | "pickup" | "return";

const PANEL_LABELS: Record<PanelKind, string> = {
  guests: "Guest waivers",
  pickup: "Pickup checklist",
  return: "Return checklist",
};

export const ReservationList = ({
  reservations,
  today,
  showPerson,
  cancellingId,
  onCancel,
}: ReservationListProps) => {
  const classes = useStyles();
  const [open, setOpen] = useState<{ id: number; kind: PanelKind } | undefined>(
    undefined
  );
  const toggle = (id: number, kind: PanelKind) =>
    setOpen((current) =>
      current?.id === id && current.kind === kind ? undefined : { id, kind }
    );

  return (
    <ul className={classes.list}>
      {reservations.map((r) => {
        const confirmed = r.status === "confirmed";
        const upcoming = confirmed && r.endDate >= today;
        // Guest waivers matter before and during a trip; checklists also after it ends.
        const panels: PanelKind[] = confirmed
          ? upcoming
            ? ["guests", "pickup", "return"]
            : ["pickup", "return"]
          : [];
        const openKind = open?.id === r.id ? open.kind : undefined;
        return (
          <li
            key={r.id}
            className={`${classes.item} ${r.status === "cancelled" ? classes.cancelled : ""}`}
          >
            <div>
              <div className={classes.dates}>{formatRange(r)}</div>
              <div className={classes.meta}>
                {r.status === "cancelled" ? (
                  <Badge tone="danger">Cancelled</Badge>
                ) : r.endDate < today ? (
                  <Badge tone="neutral">Completed</Badge>
                ) : (
                  <Badge tone="success">Confirmed</Badge>
                )}
                <span>{formatDays(r)}</span>
                <span>{r.boatName}</span>
                <span>Party of {r.partySize}</span>
                {showPerson && <strong>{r.userDisplayName}</strong>}
                <span>Booked {formatTimestamp(r.createdAt)}</span>
              </div>
              {r.notes && <p className={classes.notes}>“{r.notes}”</p>}
            </div>
            {(panels.length > 0 || upcoming) && (
              <div className={classes.actions}>
                {panels.map((kind) => (
                  <Button
                    key={kind}
                    variant={openKind === kind ? "primary" : "secondary"}
                    small
                    aria-expanded={openKind === kind}
                    onClick={() => toggle(r.id, kind)}
                  >
                    {PANEL_LABELS[kind]}
                  </Button>
                ))}
                {upcoming && (
                  <ConfirmButton
                    label="Cancel"
                    confirmLabel="Yes, cancel reservation"
                    pending={cancellingId === r.id}
                    onConfirm={() => onCancel(r.id)}
                  />
                )}
              </div>
            )}
            {openKind && (
              <div className={classes.fullWidth}>
                {openKind === "guests" ? (
                  <GuestWaiverPanel
                    reservationId={r.id}
                    partySize={r.partySize}
                  />
                ) : (
                  <ChecklistPanel
                    key={openKind}
                    reservationId={r.id}
                    step={openKind}
                  />
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};
